// src/pages/Dashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { BUILTIN_CATEGORIES, mergeCategories } from '../config/categories';
import { subscribeResources } from '../data/resources';
import { subscribeCategories } from '../data/categories';

// The Email Signature Generator is a built-in Tool. It is not stored in
// Firestore — it is hardcoded here and always shown under Internal Tools.
const EMAIL_SIGNATURE_TOOL = {
  id: '__tool_email_signature',
  category: 'internal',
  kind: 'tool',
  title: 'Email Signature Generator',
  description: 'Build and export a branded Gmail signature.',
  iconUrl: '',
  order: -1,
  path: '/tools/email-signature',
};

// The SDR Cold-Call Roleplay trainer — another built-in Internal Tool. Like the
// signature generator it is hardcoded here rather than stored in Firestore.
const SDR_ROLEPLAY_TOOL = {
  id: '__tool_sdr_roleplay',
  category: 'internal',
  kind: 'tool',
  title: 'SDR Cold-Call Roleplay',
  description: 'Practice cold calls with an AI prospect and get a scored debrief.',
  iconUrl: '',
  order: -1,
  path: '/tools/sdr-roleplay',
};

const Wrap = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px 64px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 420px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #ffffff;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: #0e8c6a;
    box-shadow: 0 0 0 3px rgba(14, 140, 106, 0.12);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

// Category tabs live in a single horizontally-scrollable row so the dashboard
// stays tidy no matter how many categories exist. Arrow buttons on either side
// nudge the strip; they hide themselves when there's nothing to scroll to.
const TabsBar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 2px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 28px;
`;

const TabsScroll = styled.div`
  display: flex;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowBtn = styled.button`
  appearance: none;
  border: none;
  background: none;
  flex-shrink: 0;
  width: 30px;
  align-self: stretch;
  color: #64748b;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease, color 0.15s ease;

  &:hover {
    color: #0e8c6a;
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }
`;

const Tab = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#64748b')};
  border-bottom: 2px solid ${(props) => (props.$active ? '#0e8c6a' : 'transparent')};
  margin-bottom: -1px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => (props.$active ? '#0e8c6a' : '#1e293b')};
  }
`;

const TabCount = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#94a3b8')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#f1f5f9')};
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-right: 2px;
`;

const Chip = styled.button`
  appearance: none;
  display: inline-flex;
  align-items: center;
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0e8c6a' : '#475569')};
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
  }
`;

const ChipCount = styled.span`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#94a3b8')};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const Tile = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
`;

const Icon = styled.img`
  width: ${(props) => (props.$sm ? '28px' : '40px')};
  height: ${(props) => (props.$sm ? '28px' : '40px')};
  border-radius: ${(props) => (props.$sm ? '6px' : '8px')};
  object-fit: contain;
  background: #ffffff;
  border: 1px solid #f1f5f9;
  padding: ${(props) => (props.$sm ? '3px' : '4px')};
  flex-shrink: 0;
`;

const MONOGRAM_COLORS = ['#0e8c6a', '#2563eb', '#7c3aed', '#d97706', '#dc2626', '#0891b2'];

function monogramColor(title) {
  const code = (title || '?').charCodeAt(0) || 0;
  return MONOGRAM_COLORS[code % MONOGRAM_COLORS.length];
}

const Monogram = styled.div`
  width: ${(props) => (props.$sm ? '28px' : '40px')};
  height: ${(props) => (props.$sm ? '28px' : '40px')};
  border-radius: ${(props) => (props.$sm ? '6px' : '8px')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(props) => (props.$sm ? '12px' : '16px')};
  font-weight: 700;
  color: #ffffff;
  background: ${(props) => props.$color};
`;

const TileBody = styled.div`
  min-width: 0;
`;

const TileTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TileDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 40px 0;
  text-align: center;
`;

const ErrorState = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
  margin: 8px 0;
`;

// ── Third Party Tools table ──────────────────────────────────────────────
const TableScroll = styled.div`
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 860px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
  vertical-align: top;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
`;

const ExtLink = styled.a`
  color: #0e8c6a;
  font-weight: 600;
  text-decoration: none;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
  }
`;

const Muted = styled.span`
  color: #94a3b8;
`;

const TeamBadge = styled.span`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  white-space: nowrap;
`;

const Pointers = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
  min-width: 220px;

  li {
    margin-bottom: 2px;
  }
`;

function hostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

// Derive a favicon/logo from the resource's URL so tools show a real icon
// without anyone having to paste an image link. Google's favicon service
// returns a 64px icon for the domain.
function faviconFor(url) {
  try {
    const host = new URL(url).hostname;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
  } catch {
    return '';
  }
}

// Shows the tool's logo (explicit iconUrl, else a favicon derived from its URL),
// and falls back to a colored monogram if there's no URL or the image fails.
function ToolIcon({ resource, $sm }) {
  const [failed, setFailed] = useState(false);
  const src = resource.iconUrl || faviconFor(resource.url);
  if (src && !failed) {
    return <Icon $sm={$sm} src={src} alt="" onError={() => setFailed(true)} />;
  }
  return (
    <Monogram $sm={$sm} $color={monogramColor(resource.title)}>
      {(resource.title || '?').charAt(0).toUpperCase()}
    </Monogram>
  );
}

function splitPointers(text) {
  return (text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function ResourceTile({ resource, onOpen }) {
  return (
    <Tile onClick={() => onOpen(resource)}>
      <ToolIcon resource={resource} />
      <TileBody>
        <TileTitle>{resource.title}</TileTitle>
        {resource.description && <TileDescription>{resource.description}</TileDescription>}
      </TileBody>
    </Tile>
  );
}

function ThirdPartyTable({ items }) {
  return (
    <TableScroll>
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>URL</Th>
            <Th>Team</Th>
            <Th>Getting started</Th>
            <Th>Official guide</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const pointers = splitPointers(r.userPointers);
            return (
              <tr key={r.id}>
                <Td>
                  <NameCell>
                    <ToolIcon resource={r} $sm />
                    <ExtLink href={r.url} target="_blank" rel="noopener noreferrer">
                      {r.title}
                    </ExtLink>
                  </NameCell>
                </Td>
                <Td>
                  {r.url ? (
                    <ExtLink href={r.url} target="_blank" rel="noopener noreferrer">
                      {hostname(r.url)}
                    </ExtLink>
                  ) : (
                    <Muted>—</Muted>
                  )}
                </Td>
                <Td>{r.team ? <TeamBadge>{r.team}</TeamBadge> : <Muted>—</Muted>}</Td>
                <Td>
                  {pointers.length ? (
                    <Pointers>
                      {pointers.map((p, i) => (
                        <li key={i}>{p}</li>
                      ))}
                    </Pointers>
                  ) : (
                    <Muted>—</Muted>
                  )}
                </Td>
                <Td>
                  {r.officialGuideUrl ? (
                    <ExtLink href={r.officialGuideUrl} target="_blank" rel="noopener noreferrer">
                      Open guide ↗
                    </ExtLink>
                  ) : (
                    <Muted>—</Muted>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
}

function Dashboard() {
  const [resources, setResources] = useState([]);
  const [customCats, setCustomCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(BUILTIN_CATEGORIES[0].key);
  const [teamFilter, setTeamFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = subscribeResources(
      (docs) => {
        setResources(docs);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        // Surface the failure instead of spinning on "Loading..." forever.
        // The common case is a Firestore permission-denied — usually the rules
        // haven't granted this user read access.
        setLoadError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view these resources. Contact an admin if this seems wrong."
            : 'Could not load resources. Please refresh and try again.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = subscribeCategories((rows) => setCustomCats(rows));
    return () => unsub();
  }, []);

  // Built-in categories first, then any custom ones (e.g. "HR").
  const categories = useMemo(() => mergeCategories(customCats), [customCats]);

  // If the active tab points at a category that no longer exists (e.g. a
  // custom one was deleted), fall back to the first category.
  useEffect(() => {
    if (!categories.some((c) => c.key === activeTab)) {
      setActiveTab(categories[0]?.key);
    }
  }, [categories, activeTab]);

  const allItems = useMemo(
    () => [...resources, EMAIL_SIGNATURE_TOOL, SDR_ROLEPLAY_TOOL],
    [resources]
  );

  const counts = useMemo(() => {
    const c = {};
    for (const cat of categories) c[cat.key] = 0;
    for (const item of allItems) if (c[item.category] != null) c[item.category] += 1;
    return c;
  }, [allItems, categories]);

  // Scroll-arrow affordances for the category tab strip.
  const tabsRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const updateCanScroll = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScroll({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);
  useEffect(() => {
    updateCanScroll();
    window.addEventListener('resize', updateCanScroll);
    return () => window.removeEventListener('resize', updateCanScroll);
  }, [updateCanScroll, categories, search, loading]);
  const nudgeTabs = (dir) => {
    const el = tabsRef.current;
    if (el) el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  const query = search.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!query) return [];
    return allItems.filter((r) => {
      const hay = `${r.title || ''} ${r.description || ''} ${r.team || ''}`.toLowerCase();
      return hay.includes(query);
    });
  }, [allItems, query]);

  const tabItems = useMemo(() => {
    return allItems
      .filter((r) => r.category === activeTab)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [allItems, activeTab]);

  // Distinct teams present among Third Party Tools (with counts), for the filter.
  const thirdPartyTeams = useMemo(() => {
    const counts = new Map();
    let total = 0;
    for (const r of allItems) {
      if (r.category !== 'thirdparty') continue;
      total += 1;
      if (r.team) counts.set(r.team, (counts.get(r.team) || 0) + 1);
    }
    const teams = Array.from(counts.entries())
      .map(([team, count]) => ({ team, count }))
      .sort((a, b) => a.team.localeCompare(b.team));
    return { teams, total };
  }, [allItems]);

  const thirdPartyVisible = useMemo(() => {
    if (teamFilter === 'all') return tabItems;
    return tabItems.filter((r) => r.team === teamFilter);
  }, [tabItems, teamFilter]);

  function handleOpen(resource) {
    if (resource.kind === 'tool') {
      navigate(resource.path);
    } else if (resource.kind === 'link') {
      if (!resource.url) return; // placeholder without a link yet — no-op
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } else if (resource.kind === 'page') {
      navigate(`/p/${resource.id}`);
    }
  }

  return (
    <Wrap>
      <PageHeader>
        <PageTitle>Resources Hub</PageTitle>
        <PageSubtitle>Everything Meltplan, in one place.</PageSubtitle>
      </PageHeader>

      <SearchInput
        type="text"
        placeholder="Search all resources..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading && <EmptyState>Loading resources...</EmptyState>}

      {!loading && loadError && <ErrorState>{loadError}</ErrorState>}

      {/* Search mode: flat results across every category */}
      {!loading && query && (
        <>
          {searchResults.length === 0 ? (
            <EmptyState>No resources match “{search}”.</EmptyState>
          ) : (
            <Grid>
              {searchResults.map((item) => (
                <ResourceTile key={item.id} resource={item} onOpen={handleOpen} />
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Browse mode: tabs per category */}
      {!loading && !query && (
        <>
          <TabsBar>
            <ArrowBtn
              type="button"
              aria-label="Scroll categories left"
              disabled={!canScroll.left}
              onClick={() => nudgeTabs(-1)}
            >
              ‹
            </ArrowBtn>
            <TabsScroll ref={tabsRef} onScroll={updateCanScroll}>
              {categories.map((cat) => {
                const active = cat.key === activeTab;
                return (
                  <Tab key={cat.key} $active={active} onClick={() => setActiveTab(cat.key)}>
                    {cat.label}
                    <TabCount $active={active}>{counts[cat.key] || 0}</TabCount>
                  </Tab>
                );
              })}
            </TabsScroll>
            <ArrowBtn
              type="button"
              aria-label="Scroll categories right"
              disabled={!canScroll.right}
              onClick={() => nudgeTabs(1)}
            >
              ›
            </ArrowBtn>
          </TabsBar>

          {tabItems.length === 0 ? (
            <EmptyState>Nothing here yet.</EmptyState>
          ) : activeTab === 'thirdparty' ? (
            <>
              {thirdPartyTeams.teams.length > 0 && (
                <FilterBar>
                  <FilterLabel>Team</FilterLabel>
                  <Chip $active={teamFilter === 'all'} onClick={() => setTeamFilter('all')}>
                    All<ChipCount $active={teamFilter === 'all'}>{thirdPartyTeams.total}</ChipCount>
                  </Chip>
                  {thirdPartyTeams.teams.map(({ team, count }) => (
                    <Chip key={team} $active={teamFilter === team} onClick={() => setTeamFilter(team)}>
                      {team}<ChipCount $active={teamFilter === team}>{count}</ChipCount>
                    </Chip>
                  ))}
                </FilterBar>
              )}
              {thirdPartyVisible.length === 0 ? (
                <EmptyState>No tools for this team.</EmptyState>
              ) : (
                <ThirdPartyTable items={thirdPartyVisible} />
              )}
            </>
          ) : (
            <Grid>
              {tabItems.map((item) => (
                <ResourceTile key={item.id} resource={item} onOpen={handleOpen} />
              ))}
            </Grid>
          )}
        </>
      )}
    </Wrap>
  );
}

export default Dashboard;
